/**
 * ComposerROI — Welcome-page "Twenty minutes. Not two quarters." section.
 *
 * Same content, routes and class namespace (`roi-fx`) as before. What changed:
 *  - The 6–8 weeks vs 20 min comparison is now drawn to scale, so the size of
 *    the gap is the visual. It plays once when scrolled into view.
 *  - Tier stack and ledgers are data-driven (no repeated SVG / markup), and the
 *    ledger gets leaders and per-line proportion bars.
 *  - SVG colours come from CSS classes instead of `hsl(var(--x))` attributes,
 *    which are not reliably resolved as presentation attributes.
 *  - `role="img"` moved from the whole visual to the SVG, so the 93% callout is
 *    readable by assistive tech.
 *  - One geometry (square panels, 1px buttons), accent-foreground on accent
 *    fills, visible keyboard focus, reduced motion respected.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";

type RevealState = "idle" | "armed" | "seen";

/** Plays an entrance once. "idle" (server render / no JS) shows the final state. */
function useReveal<T extends HTMLElement>(threshold = 0.4) {
  const ref = useRef<T>(null);
  const [state, setState] = useState<RevealState>("idle");
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setState("seen");
      return;
    }
    setState("armed");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState("seen");
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, state] as const;
}

/** Bottom (shared) → top (workspace). */
const TIERS = [
  { label: "Foundation", count: "7 mod" },
  { label: "Industry core", count: "4 mod" },
  { label: "Domain", count: "3 mod" },
  { label: "LOB", count: "2 mod" },
  { label: "Workspace", count: "thin" },
];

/** `weight` is relative duration inside its ledger, for the proportion bar. */
const MANUAL = [
  { label: "Discovery & stakeholder interviews", amt: "1.5 wk", weight: 1.5 },
  { label: "Hand-author TTL + SHACL", amt: "3 wk", weight: 3 },
  { label: "Review cycles, rework", amt: "1.5 wk", weight: 1.5 },
  { label: "Governance sign-off", amt: "1–2 wk", weight: 1.5 },
];
const COMPOSED = [
  { label: "Industry, business area, regulation, extension", amt: "6 min", weight: 6 },
  { label: "Dry-run: SHACL + OWL-RL + purity", amt: "4 min", weight: 4 },
  { label: "Review reasoning report", amt: "8 min", weight: 8 },
  { label: "Sign & publish (Ed25519)", amt: "2 min", weight: 2 },
];
const COMPOSED_MINUTES = COMPOSED.reduce((sum, line) => sum + line.weight, 0);

/** Scale for the timeline: 6 working weeks (the low end of "6–8") in minutes vs the Composer run. */
const MANUAL_MINUTES = 6 * 40 * 60;
const COMPOSER_SHARE = (COMPOSED_MINUTES / MANUAL_MINUTES) * 100;

function Ledger({
  title,
  no,
  sub,
  lines,
  total,
  stamp,
  composed,
}: {
  title: string;
  no: string;
  sub: string;
  lines: Array<{ label: string; amt: string; weight: number }>;
  total: string;
  stamp: string;
  composed?: boolean;
}) {
  const max = Math.max(...lines.map((line) => line.weight));
  return (
    <article className={`roi-ledger${composed ? " roi-ledger-composed" : ""}`}>
      <div className="roi-ledger-head">
        <h3 className="roi-ledger-title">{title}</h3>
        <span className="roi-ledger-no">{no}</span>
      </div>
      <p className="roi-ledger-sub">{sub}</p>
      <ul className="roi-lines">
        {lines.map((line) => (
          <li className="roi-lline" key={line.label}>
            <span>{line.label}</span>
            <i className="roi-lead" aria-hidden="true" />
            <span className="roi-amt">{line.amt}</span>
            <b className="roi-lbar" aria-hidden="true" style={{ "--w": line.weight / max } as CSSProperties} />
          </li>
        ))}
      </ul>
      <div className="roi-ledger-total">
        <span>Total</span>
        <span>{total}</span>
      </div>
      <p className="roi-ledger-stamp">{stamp}</p>
    </article>
  );
}

export default function ComposerROI() {
  const [stackRef, stackState] = useReveal<HTMLDivElement>(0.3);
  const [scaleRef, scaleState] = useReveal<HTMLDivElement>(0.45);

  return (
    <section id="platform" className="roi-fx">
      <div className="container">
        <div className="roi-hero-row">
          <div className="roi-hero-copy">
            <div className="section-eyebrow roi-audience">Built for the people who sign off</div>
            <h2>
              Twenty minutes.
              <br />
              Not two <em>quarters</em>.
            </h2>
            <p className="roi-sub">
              Every governed ontology your teams compose today took a specialist <b>6&ndash;8 weeks</b> to
              hand-author, per vertical, per business unit. The Composer produces the same audit-ready result —
              SHACL-validated, OWL-RL consistent, signed — in about <b>20 minutes</b>, from packs your governance
              team already vetted once.
            </p>
          </div>

          <div className="roi-tierstack-wrap" ref={stackRef} data-reveal={stackState}>
            <div className="roi-visual-kicker">
              <span>COMPOSITION PROFILE</span>
              <b>PRE-VETTED → WORKSPACE</b>
            </div>
            <svg
              className="roi-tierstack"
              viewBox="0 0 300 260"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Ontology composition tiers: foundation packs compose beneath industry, domain, and line-of-business overlays; your workspace sits on top"
            >
              <defs>
                <linearGradient id="roi-tier-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" className="roi-s1" />
                  <stop offset="1" className="roi-s2" />
                </linearGradient>
                <linearGradient id="roi-tier-current" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" className="roi-s3" />
                  <stop offset="1" className="roi-s4" />
                </linearGradient>
                <filter id="roi-shadow" x="-20%" y="-20%" width="140%" height="180%">
                  <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.28" />
                </filter>
                <filter id="roi-glow" x="-40%" y="-40%" width="180%" height="180%">
                  <feGaussianBlur stdDeviation="4" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <line x1="18" y1="242" x2="18" y2="22" className="roi-spine" />
              <text x="18" y="256" className="roi-spine-caption" textAnchor="middle">
                SHARED
              </text>
              <text x="18" y="14" className="roi-spine-caption roi-spine-caption-top" textAnchor="middle">
                COMPOSED
              </text>

              {TIERS.map((tier, i) => {
                const current = i === TIERS.length - 1;
                const x = 38 + 16 * i;
                const w = 240 - 32 * i;
                const y = 210 - 46 * i;
                const mid = y + 15;
                return (
                  <g
                    className={`roi-tier${current ? " roi-tier-current" : ""}`}
                    filter={current ? "url(#roi-glow)" : "url(#roi-shadow)"}
                    key={tier.label}
                    style={{ transitionDelay: `${i * 110}ms` }}
                  >
                    <rect x={x} y={y} width={w} height={30} className="roi-tier-rect" />
                    <circle cx={18} cy={mid} r={current ? 5 : 4} className="roi-node" />
                    <line x1={current ? 23 : 22} y1={mid} x2={x} y2={mid} className="roi-node-tie" />
                    <text x={x + 14} y={mid + 5} className="roi-tier-label">
                      {tier.label}
                    </text>
                    <text x={x + w - 12} y={mid + 5} className="roi-tier-count" textAnchor="end">
                      {tier.count}
                    </text>
                  </g>
                );
              })}

              <circle r="3" className="roi-pulse">
                <animateMotion dur="3.6s" repeatCount="indefinite" path="M 18 242 L 18 22" />
              </circle>
            </svg>

            <div className="roi-tierstack-foot">
              <span className="roi-tierstack-legend-dot" />
              <b>93%</b>
              <span>of every composition is packs your governance team already vetted once.</span>
            </div>
          </div>
        </div>

        <div className="roi-timeline" ref={scaleRef} data-reveal={scaleState}>
          <div className="roi-timeline-label">Time to a governed production candidate, drawn to scale</div>
          <div
            className="roi-scale"
            role="img"
            aria-label="Manual engineering takes 6 to 8 weeks. The Composer takes about 20 minutes. Bars are drawn to the same scale, so the Composer bar is a thin sliver."
          >
            <div className="roi-scale-row">
              <span className="roi-scale-name">Manual engineering</span>
              <div className="roi-track">
                <div className="roi-bar roi-bar-before" />
                <span className="roi-bar-text roi-bar-text-before">
                  <b>6&ndash;8 weeks</b> hand-authored per vertical
                </span>
              </div>
            </div>
            <div className="roi-scale-row">
              <span className="roi-scale-name roi-scale-name-after">Composer</span>
              <div className="roi-track">
                <div className="roi-bar roi-bar-after" style={{ "--share": `${COMPOSER_SHARE}%` } as CSSProperties} />
                <span className="roi-bar-text roi-bar-text-after" style={{ "--share": `${COMPOSER_SHARE}%` } as CSSProperties}>
                  <b>~20 min</b> from vetted packs
                </span>
              </div>
            </div>
          </div>
          <p className="roi-timeline-caption">
            Same outcome, same rigor — every run still passes <code>SHACL conforms</code>,{" "}
            <code>OWL-RL consistent</code>, <code>industry-pure</code> before anything saves. Scale compares 240
            working hours with one 20-minute run.
          </p>
        </div>

        <div className="roi-stats">
          <div className="roi-stat">
            <div className="roi-stat-k">Consulting hours avoided</div>
            <div className="roi-stat-v">
              ~240 <small>hrs / vertical</small>
            </div>
            <div className="roi-stat-d">
              A specialist's 6&ndash;8 week build, replaced by composing pre-vetted foundation, industry and
              regulatory packs.
            </div>
          </div>
          <div className="roi-stat">
            <div className="roi-stat-k">Reuse across business units</div>
            <div className="roi-stat-v">
              2nd, 3rd deploy <small>near-instant</small>
            </div>
            <div className="roi-stat-d">
              Foundation and universal packs — party, geography, regulatory, risk — are shared, not re-authored per
              unit.
            </div>
          </div>
          <div className="roi-stat">
            <div className="roi-stat-k">Audit exposure</div>
            <div className="roi-stat-v">
              Signed <small>day one</small>
            </div>
            <div className="roi-stat-d">
              Every composition is Ed25519-signed with a full provenance and reasoning trail, not retrofitted before a
              review.
            </div>
          </div>
        </div>

        <div className="roi-ledgers">
          <Ledger
            title="Manual build"
            no="NO. 0001 · PER VERTICAL"
            sub="Hand-authored, one specialist, one business unit"
            lines={MANUAL}
            total="6–8 weeks"
            stamp="STATUS: repeats in full for every new vertical"
          />
          <Ledger
            composed
            title="Composed"
            no="NO. 0002 · STUDIO"
            sub="Vetted packs, dry-run validated, signed on save"
            lines={COMPOSED}
            total={`~${COMPOSED_MINUTES} minutes`}
            stamp="STATUS: 2nd and 3rd deploy reuse the same foundation packs"
          />
        </div>

        <div className="roi-cta-row">
          <Link to="/studio/composer" className="roi-btn">
            Open Studio → Composer
          </Link>
          <Link to="/studio/composer" hash="reasoning" className="roi-btn roi-btn-ghost">
            See the reasoning report
          </Link>
          <span className="roi-cta-note">semantic_composer · Pro and Enterprise</span>
        </div>
      </div>

      <style>{`
        .roi-fx{padding:96px 0;border-top:1px solid hsl(var(--border))}
        .roi-fx .container{max-width:1280px;margin:0 auto;padding:0 32px}
        .roi-fx{--roi-mono:'IBM Plex Mono',ui-monospace,monospace;--roi-ease:cubic-bezier(.2,.7,.1,1)}

        /* Hero row */
        .roi-fx .roi-hero-row{display:grid;grid-template-columns:minmax(360px,.92fr) minmax(480px,1.08fr);align-items:stretch;margin-bottom:48px;border:1px solid hsl(var(--border));background:hsl(var(--panel)/.22)}
        .roi-fx .roi-hero-copy{display:flex;flex-direction:column;justify-content:center;padding:clamp(40px,5vw,72px);border-right:1px solid hsl(var(--border))}
        .roi-fx h2{max-width:1000px;margin:8px 0 18px;font-size:clamp(40px,5vw,84px);font-weight:500;line-height:.96;letter-spacing:-.035em;text-wrap:balance}
        .roi-fx h2 em{font-style:normal;color:hsl(var(--accent))}
        .roi-fx .roi-audience{margin-bottom:0}
        .roi-fx .roi-sub{max-width:640px;margin:0;color:hsl(var(--muted));font-size:16px;line-height:1.6}
        .roi-fx .roi-sub b{color:hsl(var(--text));font-weight:600}

        /* Tier stack */
        .roi-fx .roi-tierstack-wrap{position:relative;display:flex;min-width:0;min-height:570px;flex-direction:column;justify-content:center;gap:18px;padding:62px clamp(30px,4vw,58px) 30px;overflow:hidden;isolation:isolate;background-color:hsl(var(--bg));background-image:linear-gradient(hsl(var(--border)/.35) 1px,transparent 1px),linear-gradient(90deg,hsl(var(--border)/.35) 1px,transparent 1px);background-size:28px 28px}
        .roi-fx .roi-tierstack-wrap::after{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;background:radial-gradient(circle at 52% 44%,hsl(var(--accent)/.12),transparent 58%)}
        .roi-fx .roi-visual-kicker{position:absolute;z-index:1;top:22px;right:24px;left:24px;display:flex;justify-content:space-between;gap:20px;color:hsl(var(--muted));font:10px/1 var(--roi-mono);letter-spacing:.13em}
        .roi-fx .roi-visual-kicker b{color:hsl(var(--accent));font-weight:500}
        .roi-fx .roi-tierstack,.roi-fx .roi-tierstack-foot{position:relative;z-index:1}
        .roi-fx .roi-tierstack{display:block;width:100%;height:auto;max-height:390px;overflow:visible;filter:drop-shadow(0 24px 34px hsl(var(--bg)/.34))}
        .roi-fx .roi-s1{stop-color:hsl(var(--panel));stop-opacity:.96}
        .roi-fx .roi-s2{stop-color:hsl(var(--panel));stop-opacity:.72}
        .roi-fx .roi-s3{stop-color:hsl(var(--accent));stop-opacity:.28}
        .roi-fx .roi-s4{stop-color:hsl(var(--accent));stop-opacity:.1}
        .roi-fx .roi-tier{transition:opacity .6s ease,transform .6s var(--roi-ease)}
        .roi-fx [data-reveal="armed"] .roi-tier{opacity:0;transform:translateY(16px)}
        .roi-fx .roi-tier-rect{fill:url(#roi-tier-fill);stroke:hsl(var(--border));transition:filter .25s ease}
        .roi-fx .roi-tier-current .roi-tier-rect{fill:url(#roi-tier-current);stroke:hsl(var(--accent));stroke-width:1.25}
        .roi-fx .roi-tierstack:hover .roi-tier-rect{filter:brightness(1.06)}
        .roi-fx .roi-tierstack:hover .roi-tier-current .roi-tier-rect{filter:brightness(1.12) drop-shadow(0 0 6px hsl(var(--accent)/.55))}
        .roi-fx .roi-spine{stroke:hsl(var(--border));stroke-width:1;stroke-dasharray:2 4}
        .roi-fx .roi-spine-caption{font:8.5px var(--roi-mono);fill:hsl(var(--muted));letter-spacing:.14em;text-transform:uppercase}
        .roi-fx .roi-spine-caption-top{fill:hsl(var(--accent))}
        .roi-fx .roi-node{fill:hsl(var(--panel));stroke:hsl(var(--border));stroke-width:1}
        .roi-fx .roi-node-tie{stroke:hsl(var(--border));stroke-width:1}
        .roi-fx .roi-tier-current .roi-node{fill:hsl(var(--accent));stroke:hsl(var(--accent))}
        .roi-fx .roi-tier-current .roi-node-tie{stroke:hsl(var(--accent));stroke-width:1.5}
        .roi-fx .roi-tier-label{font:500 11.5px var(--roi-mono);fill:hsl(var(--text));letter-spacing:.01em}
        .roi-fx .roi-tier-current .roi-tier-label{fill:hsl(var(--accent));font-weight:600}
        .roi-fx .roi-tier-count{font:9.5px var(--roi-mono);fill:hsl(var(--muted));letter-spacing:.04em;text-transform:uppercase}
        .roi-fx .roi-tier-current .roi-tier-count{fill:hsl(var(--accent))}
        .roi-fx .roi-pulse{fill:hsl(var(--accent));filter:drop-shadow(0 0 4px hsl(var(--accent)/.9))}
        .roi-fx .roi-tierstack-foot{display:grid;grid-template-columns:8px auto 1fr;align-items:center;gap:12px;padding:17px 18px;border:1px solid hsl(var(--border));background:hsl(var(--panel)/.96);color:hsl(var(--muted));font-size:13px;line-height:1.45}
        .roi-fx .roi-tierstack-foot b{color:hsl(var(--accent));font:600 28px/1 var(--roi-mono)}
        .roi-fx .roi-tierstack-legend-dot{width:8px;height:8px;border-radius:50%;background:hsl(var(--accent));box-shadow:0 0 0 3px hsl(var(--accent)/.16)}

        /* Timeline, drawn to scale */
        .roi-fx .roi-timeline{margin-bottom:20px;padding:36px 40px 32px;border:1px solid hsl(var(--border));background:hsl(var(--panel))}
        .roi-fx .roi-timeline-label{margin-bottom:26px;color:hsl(var(--muted));font:12px var(--roi-mono);letter-spacing:.06em;text-transform:uppercase}
        .roi-fx .roi-scale{display:grid;gap:14px}
        .roi-fx .roi-scale-row{display:grid;grid-template-columns:150px 1fr;align-items:center;gap:20px}
        .roi-fx .roi-scale-name{color:hsl(var(--muted));font:11px var(--roi-mono);letter-spacing:.08em;text-transform:uppercase}
        .roi-fx .roi-scale-name-after{color:hsl(var(--accent))}
        .roi-fx .roi-track{position:relative;height:54px}
        .roi-fx .roi-bar{position:absolute;top:0;bottom:0;left:0}
        .roi-fx .roi-bar-before{right:0;border:1px solid hsl(var(--border));background:repeating-linear-gradient(135deg,hsl(var(--border)/.55) 0 1px,transparent 1px 8px),hsl(var(--bg)/.5);transform-origin:left;transition:transform 1.5s var(--roi-ease)}
        .roi-fx .roi-bar-after{width:max(3px,var(--share));background:hsl(var(--accent));box-shadow:0 0 0 4px hsl(var(--accent)/.16),0 0 22px hsl(var(--accent)/.6);transition:opacity .5s ease 1.4s}
        .roi-fx .roi-bar-text{position:absolute;top:50%;display:flex;align-items:baseline;gap:10px;transform:translateY(-50%);white-space:nowrap;color:hsl(var(--muted));font:13px var(--roi-mono);transition:opacity .5s ease}
        .roi-fx .roi-bar-text b{font-size:16px;font-weight:600;color:hsl(var(--text))}
        .roi-fx .roi-bar-text-before{left:20px;transition-delay:.7s}
        .roi-fx .roi-bar-text-after{left:calc(max(3px,var(--share)) + 18px);transition-delay:1.5s}
        .roi-fx .roi-bar-text-after b{color:hsl(var(--accent))}
        .roi-fx .roi-bar-text-after::before{content:"";width:22px;height:1px;align-self:center;background:hsl(var(--accent))}
        .roi-fx [data-reveal="armed"] .roi-bar-before{transform:scaleX(0)}
        .roi-fx [data-reveal="armed"] .roi-bar-after,.roi-fx [data-reveal="armed"] .roi-bar-text{opacity:0;transition-delay:0s}
        .roi-fx .roi-timeline-caption{max-width:78ch;margin:20px 0 0;color:hsl(var(--muted));font-size:13px;line-height:1.55}
        .roi-fx .roi-timeline-caption code{padding:1px 6px;background:hsl(var(--bg)/.6);color:hsl(var(--text));font-family:var(--roi-mono)}

        /* Stats */
        .roi-fx .roi-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin:32px 0 48px}
        .roi-fx .roi-stat{padding:24px 22px;border:1px solid hsl(var(--border));border-top:2px solid hsl(var(--accent)/.7);background:hsl(var(--panel))}
        .roi-fx .roi-stat-k{margin-bottom:10px;color:hsl(var(--muted));font:12px var(--roi-mono);letter-spacing:.06em;text-transform:uppercase}
        .roi-fx .roi-stat-v{margin-bottom:6px;color:hsl(var(--text));font-size:28px;font-weight:650;letter-spacing:-.01em;font-variant-numeric:tabular-nums}
        .roi-fx .roi-stat-v small{margin-left:4px;color:hsl(var(--muted));font-size:15px;font-weight:500}
        .roi-fx .roi-stat-d{color:hsl(var(--muted));font-size:13.5px;line-height:1.5}

        /* Ledgers */
        .roi-fx .roi-ledgers{display:grid;grid-template-columns:1fr 1fr;align-items:start;gap:20px;margin-bottom:44px}
        .roi-fx .roi-ledger{padding:26px 26px 20px;border:1px solid hsl(var(--border));background:hsl(var(--panel));font-family:var(--roi-mono)}
        .roi-fx .roi-ledger-composed{border-color:hsl(var(--accent)/.55);background:linear-gradient(180deg,hsl(var(--accent)/.08),hsl(var(--panel)) 140px)}
        .roi-fx .roi-ledger-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-bottom:4px}
        .roi-fx .roi-ledger-title{margin:0;color:hsl(var(--text));font:600 13px var(--roi-mono);letter-spacing:.01em;text-transform:uppercase}
        .roi-fx .roi-ledger-composed .roi-ledger-title{color:hsl(var(--accent))}
        .roi-fx .roi-ledger-no{color:hsl(var(--muted));font-size:11px}
        .roi-fx .roi-ledger-sub{margin:0 0 16px;padding-bottom:16px;border-bottom:1px dashed hsl(var(--border));color:hsl(var(--muted));font-size:11.5px}
        .roi-fx .roi-lines{margin:0;padding:0;list-style:none}
        .roi-fx .roi-lline{display:grid;grid-template-columns:auto 1fr auto;align-items:baseline;column-gap:10px;padding:8px 0 7px;color:hsl(var(--muted));font-size:13px}
        .roi-fx .roi-lead{align-self:end;height:1px;margin-bottom:4px;background:radial-gradient(circle,hsl(var(--border)) 1px,transparent 1.5px) 0 0/6px 2px repeat-x}
        .roi-fx .roi-amt{color:hsl(var(--text));font-variant-numeric:tabular-nums}
        .roi-fx .roi-ledger-composed .roi-amt{color:hsl(var(--accent))}
        .roi-fx .roi-lbar{grid-column:1/-1;display:block;width:calc(var(--w)*100%);height:2px;margin-top:6px;background:hsl(var(--muted)/.45)}
        .roi-fx .roi-ledger-composed .roi-lbar{background:hsl(var(--accent)/.7)}
        .roi-fx .roi-ledger-total{display:flex;justify-content:space-between;margin-top:10px;padding-top:12px;border-top:1px solid hsl(var(--text));color:hsl(var(--text));font-size:15px;font-weight:700}
        .roi-fx .roi-ledger-composed .roi-ledger-total{border-top-color:hsl(var(--accent));color:hsl(var(--accent))}
        .roi-fx .roi-ledger-stamp{margin:14px 0 0;color:hsl(var(--muted));font-size:10.5px;letter-spacing:.03em}

        /* CTA */
        .roi-fx .roi-cta-row{display:flex;flex-wrap:wrap;align-items:center;gap:18px}
        .roi-fx .roi-btn{display:inline-flex;min-height:46px;align-items:center;padding:0 20px;border:1px solid hsl(var(--accent));border-radius:1px;background:hsl(var(--accent));color:hsl(var(--accent-foreground));font:600 11px/1 var(--roi-mono);letter-spacing:.06em;text-decoration:none;text-transform:uppercase;transition:background .2s ease,border-color .2s ease}
        .roi-fx .roi-btn:hover{background:hsl(var(--accent)/.9)}
        .roi-fx .roi-btn-ghost{border-color:hsl(var(--border));background:transparent;color:hsl(var(--text))}
        .roi-fx .roi-btn-ghost:hover{border-color:hsl(var(--accent)/.55);background:hsl(var(--accent)/.06)}
        .roi-fx .roi-btn:focus-visible{outline:2px solid hsl(var(--accent));outline-offset:3px}
        .roi-fx .roi-cta-note{color:hsl(var(--muted));font:13px var(--roi-mono)}

        @media (max-width:900px){
          .roi-fx .roi-hero-row{grid-template-columns:1fr}
          .roi-fx .roi-hero-copy{border-right:0;border-bottom:1px solid hsl(var(--border))}
          .roi-fx .roi-stats,.roi-fx .roi-ledgers{grid-template-columns:1fr}
        }
        @media (max-width:720px){
          .roi-fx .roi-timeline{padding:28px 20px 24px}
          .roi-fx .roi-scale-row{grid-template-columns:1fr;gap:8px}
          .roi-fx .roi-track{height:64px}
          .roi-fx .roi-bar-text{flex-direction:column;align-items:flex-start;gap:2px;white-space:normal}
          .roi-fx .roi-bar-text-after::before{display:none}
          .roi-fx .roi-bar-text-before{left:14px}
          .roi-fx .roi-bar-text-after{left:calc(max(3px,var(--share)) + 14px)}
        }
        @media (max-width:600px){
          .roi-fx .container{padding:0 18px}
          .roi-fx .roi-hero-copy{padding:34px 22px}
          .roi-fx .roi-tierstack-wrap{min-height:470px;padding:58px 18px 22px}
          .roi-fx .roi-visual-kicker{right:16px;left:16px}
          .roi-fx .roi-tierstack-foot{grid-template-columns:8px auto}
          .roi-fx .roi-tierstack-foot span:last-child{grid-column:1/-1}
          .roi-fx .roi-ledger{padding:22px 18px 18px}
          .roi-fx .roi-ledger-head{flex-direction:column;gap:2px}
        }
        @media (prefers-reduced-motion:reduce){
          .roi-fx .roi-pulse{display:none}
          .roi-fx .roi-tier,.roi-fx .roi-bar,.roi-fx .roi-bar-text,.roi-fx .roi-btn{transition:none}
        }
      `}</style>
    </section>
  );
}